import {
  Order,
  OrderDetail,
  OrderCreate,
  OrderStatusUpdate,
  OrderPaymentUpdate,
  OrderListResponse,
} from "../types/orders";

import apiClient from "./api";

class OrdersService {
  /**
   * Get all orders with filtering options
   */
  async getOrders(params: {
    skip?: number;
    limit?: number;
    status_id?: number;
    customer_id?: number;
    start_date?: string;
    end_date?: string;
    search?: string;
    order_type?: string;
  }): Promise<OrderListResponse> {
    return await apiClient.get<OrderListResponse>("/orders", { params });
  }

  /**
   * Get active orders (new, processing, ready)
   */
  async getActiveOrders(): Promise<Order[]> {
    return await apiClient.get<Order[]>("/orders/active");
  }

  /**
   * Get order details by ID
   */
  async getOrderById(id: number): Promise<OrderDetail> {
    return await apiClient.get<OrderDetail>(`/orders/${id}`);
  }

  /**
   * Create a new order
   */
  async createOrder(orderData: OrderCreate): Promise<Order> {
    return await apiClient.post<Order>("/orders", orderData);
  }

  /**
   * Update order status
   */
  async updateOrderStatus(
    id: number,
    statusData: OrderStatusUpdate,
  ): Promise<Order> {
    return await apiClient.put<Order>(`/orders/${id}/status`, statusData);
  }

  /**
   * Update order payment
   */
  async updateOrderPayment(
    id: number,
    paymentData: OrderPaymentUpdate,
  ): Promise<Order> {
    return await apiClient.put<Order>(`/orders/${id}/payment`, paymentData);
  }

  /**
   * Assign delivery driver
   */
  async assignDelivery(orderData: {
    order_id: number;
    driver_id: number;
    notes?: string;
  }): Promise<any> {
    return await apiClient.post<any>("/orders/delivery-assignments", orderData);
  }

  /**
   * Update delivery status
   */
  async updateDeliveryStatus(
    assignmentId: number,
    statusData: { status: string; notes?: string },
  ): Promise<any> {
    return await apiClient.put<any>(
      `/orders/delivery-assignments/${assignmentId}`,
      statusData,
    );
  }

  /**
   * Record payment transaction
   */
  async recordPayment(paymentData: {
    order_id: number;
    amount: number;
    payment_method_id: number;
    transaction_reference?: string;
    transaction_status?: string;
    last_four?: string;
    card_type?: string;
  }): Promise<any> {
    return await apiClient.post<any>("/orders/payments", paymentData);
  }

  /**
   * Process refund
   */
  async processRefund(refundData: {
    transaction_id: number;
    amount: number;
    reason: string;
    reference?: string;
  }): Promise<void> {
    return await apiClient.post<void>("/orders/refunds", refundData);
  }
}

// Create singleton instance
export const ordersService = new OrdersService();
